import { Component, Input, ChangeDetectionStrategy } from '@angular/core'
import { type LucideIconData, LucideAngularModule } from 'lucide-angular'
import { SVG_TO_LUCIDE_FALLBACK } from '@/styles/components/icon/icons'

const ICON_NAME_MAP: Record<string, string> = {
    access_time: 'clock-icon',
    cancel: 'close',
    chat_bubble_outline: 'forum',
    check_circle: 'check-circle-icon',
    content_copy: 'description',
    delete_outline: 'delete-outline',
    dashboard: 'grid-view',
    file_upload: 'cloud-upload',
    info: 'info-icon',
    keyboard_arrow_down: 'arrow-drop-down',
    keyboard_arrow_up: 'arrow-drop-up',
    mail: 'email',
    message: 'forum',
    people: 'people-outline',
    play_circle: 'visibility',
    schedule: 'clock-icon',
    shopping_bag: 'inventory',
    supervised_user_circle: 'groups',
    support_agent: 'contact-support',
    table_view: 'table-chart',
    translate: 'language',
    upload: 'cloud-upload',
    upload_file: 'cloud-upload',
}

const SIZE_MAP: Record<string, number> = {
    '2xs': 10,
    xs: 12,
    sm: 16,
    md: 20,
    lg: 24,
    xl: 32,
}

@Component({
    selector: 'app-svg-icon',
    standalone: true,
    imports: [LucideAngularModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './svg-icon.component.scss',
    template: `
        @if (lucideIcon) {
            <lucide-angular
                [img]="lucideIcon"
                [style.width.px]="sizeValue"
                [style.height.px]="sizeValue"
                [strokeWidth]="2"
                class="inline-block shrink-0"
            />
        } @else {
            <span
                class="inline-block shrink-0"
                [style.width.px]="sizeValue"
                [style.height.px]="sizeValue"
                [style.backgroundColor]="'currentColor'"
                [style.-webkit-mask-image]="maskUrl"
                [style.mask-image]="maskUrl"
                [style.-webkit-mask-size]="'contain'"
                [style.mask-size]="'contain'"
                [style.-webkit-mask-repeat]="'no-repeat'"
                [style.mask-repeat]="'no-repeat'"
                [style.-webkit-mask-position]="'center'"
                [style.mask-position]="'center'"
            ></span>
        }
    `,
})
export class SvgIconComponent {
    @Input({ required: true }) name = ''
    @Input() size: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'lg'

    get sizeValue(): number {
        return SIZE_MAP[this.size] || 24
    }

    get lucideIcon(): LucideIconData | null {
        const fallback = SVG_TO_LUCIDE_FALLBACK[this.name]
        return fallback ?? null
    }

    get resolvedName(): string {
        if (ICON_NAME_MAP[this.name]) {
            return ICON_NAME_MAP[this.name]
        }
        return this.name.replace(/_/g, '-')
    }

    get maskUrl(): string {
        return `url(/assets/icons/${this.resolvedName}.svg)`
    }
}
